import * as Sentry from '@sentry/nextjs';
// Api
import { GET_STORE_BY_SLUG } from 'api/store/queries';
// Types
import type {
  GetStoreBySlug,
  GetStoreBySlugVariables,
  GetStoreBySlug_store,
} from 'api/store/types/GetStoreBySlug';
// Helpers
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';

export type AthleteContentTab =
  | 'memorabilia'
  | 'merch'
  | 'media'
  | 'streams'
  | 'experiences'
  | 'news'
  | 'events';

export const athleteTabHasContent = (
  store: GetStoreBySlug_store,
  tab: AthleteContentTab
): boolean => {
  switch (tab) {
    case 'memorabilia':
      // "Products" no longer has its own tab — its items now show
      // alongside memorabilia here.
      return store.hasMemorabilia || store.hasPureProducts;
    case 'merch':
      return store.hasMerch;
    case 'media':
      return store.hasMediaPost;
    case 'streams':
      return store.hasStreams || store.hasActiveStreams;
    case 'experiences':
      return store.hasExperiences;
    case 'news':
      return store.hasArticle;
    case 'events':
      return store.hasTicketEvents;
  }
};

export const getAthleteTabPageProps = async (
  ctx: ApolloPageContext,
  tab: AthleteContentTab
): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);

  if (!ctx.apolloClient || !athleteSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetStoreBySlug,
      GetStoreBySlugVariables
    >({
      query: GET_STORE_BY_SLUG,
      variables: {
        input: {
          slug: athleteSlug,
        },
        withFollowerCount: false,
      },
    });

    if (!data?.store || !athleteTabHasContent(data.store, tab)) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: `[athleteSlug]/${tab}` },
    });
    return {};
  }

  return {};
};
